// ---------------------------------------------------------------------------
// API Call Logger — Global fetch interceptor for /api/* calls
// ---------------------------------------------------------------------------

const MAX_LOG_ENTRIES = 100;
const MAX_BODY_LENGTH = 5000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApiCallEntry {
  id: string;
  timestamp: number;
  method: string;
  endpoint: string;
  status: number | null;
  durationMs: number;
  requestBody?: unknown;
  responseBody?: unknown;
  error?: string;
}

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let originalFetch: typeof window.fetch | null = null;
let installed = false;
const logBuffer: ApiCallEntry[] = [];
const listeners = new Set<(entry: ApiCallEntry) => void>();

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function shouldLog(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.startsWith('/api/');
  } catch {
    return url.startsWith('/api/') || url.startsWith('/api?');
  }
}

function extractEndpoint(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

function truncateBody(body: unknown): unknown {
  if (body === undefined || body === null) return body;
  const str = typeof body === 'string' ? body : JSON.stringify(body);
  if (str && str.length > MAX_BODY_LENGTH) {
    return str.slice(0, MAX_BODY_LENGTH) + '...[truncated]';
  }
  return body;
}

async function safeReadBody(response: Response): Promise<unknown> {
  try {
    const cloned = response.clone();
    const text = await cloned.text();
    try {
      return truncateBody(JSON.parse(text));
    } catch {
      return truncateBody(text);
    }
  } catch {
    return '[unreadable]';
  }
}

function extractRequestBody(init?: RequestInit): unknown {
  if (!init?.body) return undefined;
  if (typeof init.body === 'string') {
    try {
      return truncateBody(JSON.parse(init.body));
    } catch {
      return truncateBody(init.body);
    }
  }
  return '[non-string body]';
}

function notify(entry: ApiCallEntry): void {
  listeners.forEach((fn) => {
    try {
      fn(entry);
    } catch {
      // listener errors should not break the logger
    }
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function installApiCallLogger(): void {
  if (installed || typeof window === 'undefined') return;

  originalFetch = window.fetch.bind(window);
  const savedFetch = originalFetch;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

    if (!shouldLog(url)) {
      return savedFetch(input, init);
    }

    const entry: ApiCallEntry = {
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      method: init?.method?.toUpperCase() || 'GET',
      endpoint: extractEndpoint(url),
      status: null,
      durationMs: 0,
      requestBody: extractRequestBody(init),
    };

    const start = performance.now();

    try {
      const response = await savedFetch(input, init);
      entry.durationMs = Math.round(performance.now() - start);
      entry.status = response.status;
      entry.responseBody = await safeReadBody(response);
      addEntry(entry);
      return response;
    } catch (err: any) {
      entry.durationMs = Math.round(performance.now() - start);
      entry.error = err.message || String(err);
      addEntry(entry);
      throw err;
    }
  };

  installed = true;
}

export function uninstallApiCallLogger(): void {
  if (!installed || !originalFetch) return;
  window.fetch = originalFetch;
  originalFetch = null;
  installed = false;
}

function addEntry(entry: ApiCallEntry): void {
  logBuffer.unshift(entry);
  if (logBuffer.length > MAX_LOG_ENTRIES) {
    logBuffer.pop();
  }
  notify(entry);
}

export function getApiCallLog(): ApiCallEntry[] {
  return [...logBuffer];
}

export function clearApiCallLog(): void {
  logBuffer.length = 0;
}

export function onApiCall(listener: (entry: ApiCallEntry) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
